import React from 'react';

import { useCurrentUser } from 'Auth';

import { ConfirmDialog } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import { useScreenModalV2 } from '@gogame/screens';

import InfluencerCampaignDetails from 'scenes/Influencers/InfluencersCampaigns/components/InfluencerCampaignDetails';
import {
  useCreateInfluencerCampaign,
  useUpdateInfluencerCampaign,
  useLoadInfluencerCampaign,
} from 'scenes/Influencers/InfluencersCampaigns/data';

import InfluencerCampaignForm from './InfluencerCampaignForm';
import {
  GENERATE_NAME_AUTOMATICALLY,
  NAME,
  OWNER,
  CURRENCY,
  REWARD_PACKAGE,
  ADDITIONAL_REWARD_PACKAGE,
  PROMO_CODE,
  COST_MODEL,
  IS_RE_ENGAGEMENT,
} from './constants';

const normalizeSubmitValues = ({
  [NAME]: name,
  [GENERATE_NAME_AUTOMATICALLY]: generateNameAutomatically,
  ...values
}) => ({
  ...values,
  [NAME]: generateNameAutomatically ? null : name,
});

const getMultipleRewards = ({
  [REWARD_PACKAGE]: rewardPackage,
  [ADDITIONAL_REWARD_PACKAGE]: additionalRewardPackage,
  [PROMO_CODE]: promoCode,
}) =>
  [
    rewardPackage && 'Reward packages',
    additionalRewardPackage && 'Additional reward packages',
    promoCode && 'Promocode rewards',
  ].filter((i) => i);

export const InfluencerCampaignFormCreate = ({
  onSubmit,
  onClose,
  onExited,
  sourceValues,
  open,
  isLoading,
  initialValid = false,
  active = true,
}) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const [createInfluencerCampaign, { isLoading: isCreating }] =
    useCreateInfluencerCampaign();
  const user = useCurrentUser();
  const showModal = useScreenModalV2(ConfirmDialog);

  const handleSubmit = async (values = {}) => {
    const multipleRewards = getMultipleRewards(values);
    if (multipleRewards.length > 1) {
      await showModal({
        title: 'Campaign has multiple rewards',
        confirmActionLabel: 'Create with multiple rewards',
        bodyTextList: multipleRewards,
      });
    }

    const createdMasterCampaign = await createInfluencerCampaign(
      normalizeSubmitValues(values),
      ({ id: existId }) => {
        openChildComponent(InfluencerCampaignDetails, { id: existId });
      }
    );

    onSubmit(createdMasterCampaign);
  };

  const initialValues = {
    ...sourceValues,
    [GENERATE_NAME_AUTOMATICALLY]: true,
    [OWNER]: user.id,
    [IS_RE_ENGAGEMENT]: true,
    [COST_MODEL]: {
      [CURRENCY]: 'usd',
      ...sourceValues?.[COST_MODEL],
    },
  };

  return (
    <InfluencerCampaignForm
      title={'Add Influencer Campaign'}
      loading={isCreating || isLoading}
      active={active}
      onClose={onClose}
      onExited={onExited}
      open={open}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      initialValid={initialValid}
    >
      {childComponent}
    </InfluencerCampaignForm>
  );
};

export const InfluencerCampaignFormDuplicate = ({ id, ...props }) => {
  const { data, isLoading } = useLoadInfluencerCampaign(id);

  return (
    <InfluencerCampaignFormCreate
      {...props}
      initialValid={true}
      isLoading={isLoading}
      active={!!data}
      sourceValues={data}
    />
  );
};

export const InfluencerCampaignFormEdit = ({
  id,
  onClose,
  onSubmit,
  onExited,
  open,
}) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const { data, isLoading } = useLoadInfluencerCampaign(id);
  const [updateInfluencerCampaign, { isLoading: isUpdating }] =
    useUpdateInfluencerCampaign();
  const showModal = useScreenModalV2(ConfirmDialog);

  const handleSubmit = async (values) => {
    const multipleRewards = getMultipleRewards(values);
    if (multipleRewards.length > 1) {
      await new Promise(async (resolve) => {
        await showModal({
          title: 'Campaign has multiple rewards',
          confirmActionLabel: 'Save with multiple rewards',
          bodyTextList: multipleRewards,
        });
        resolve();
      });
    }

    const response = await updateInfluencerCampaign(
      normalizeSubmitValues(values),
      ({ id: existId }) => {
        openChildComponent(InfluencerCampaignDetails, { id: existId });
      }
    );
    onSubmit(response);
  };

  return (
    <InfluencerCampaignForm
      title={'Edit Influencer Campaign'}
      subTitle={data?.[NAME]}
      entityId={id}
      loading={isLoading || isUpdating}
      onClose={onClose}
      onExited={onExited}
      open={open}
      initialValues={data}
      active={!!data}
      onSubmit={handleSubmit}
      isEdit={true}
      initialValid={true}
    >
      {childComponent}
    </InfluencerCampaignForm>
  );
};
